import React from "react";
import { Link } from "gatsby";

import Header from "../../components/Header";
import Footer from "../../components/Footer";
import Seo from "../../components/Seo";
import TrustpilotInline from "../../components/TrustpilotInline";

import "../../../scss/main.scss";

const imagePath = "/assets/marketing-site/images";

const supportAreas = [
	[
		"01",
		"Treatment-day support",
		"A carer can help someone prepare for appointments, travel safely, settle back at home and follow the practical guidance provided by their clinical team.",
	],
	[
		"02",
		"Meals and hydration",
		"Gentle encouragement, familiar food and flexible mealtimes can help when appetite, taste or energy changes during treatment.",
	],
	[
		"03",
		"Medication routines",
		"Prompts or safe administration can follow the agreed care plan, alongside careful recording and escalation of any concerns.",
	],
	[
		"04",
		"Personal care with dignity",
		"Discreet help with washing, dressing, continence and skin care can flex around pain, fatigue and how the person feels that day.",
	],
	[
		"05",
		"Energy and everyday life",
		"A carer can take care of practical tasks while helping the person save their energy for the routines, people and moments that matter most.",
	],
	[
		"06",
		"Reassurance for family",
		"Consistent one-to-one support helps families stay informed and gives everyone space to be a partner, child or friend rather than only a carer.",
	],
];

const changesToShare = [
	"Pain that is new, changing or difficult to manage",
	"Eating or drinking much less than usual",
	"New confusion, unusual drowsiness or a marked change in mood",
	"Breathlessness, fever or signs of infection",
	"Changes to mobility, balance or ability to manage personal care",
	"Treatment side effects that are worsening or causing concern",
];

const careStages = [
	[
		"During active treatment",
		"Care can be arranged around chemotherapy, radiotherapy, surgery or other treatment, with extra support on difficult days and space for independence when energy returns.",
	],
	[
		"Recovery and rehabilitation",
		"After treatment or surgery, support can focus on rebuilding routines, following clinical advice and regaining confidence safely at home.",
	],
	[
		"Living with cancer",
		"For people living with cancer over the longer term, care can adapt to changing symptoms while protecting normality, relationships and the person's own priorities.",
	],
	[
		"Palliative support",
		"When care is focused on comfort and quality of life, a live-in carer can work alongside community nurses, GPs and specialist palliative teams to provide calm, practical support at home.",
	],
];

const homePrinciples = [
	[
		"Keep rest close, not isolated",
		"Create comfortable places to rest near everyday family life, with useful items, drinks and communication within easy reach.",
	],
	[
		"Make food flexible",
		"Keep a range of simple options available and adapt portions, timings and textures around appetite and clinical guidance.",
	],
	[
		"Organise the clinical routine",
		"Use one clear place for appointment details, medication records and contact numbers so everyone knows what is happening next.",
	],
	[
		"Review as needs change",
		"A Care Manager can revisit risks, routines and support when treatment changes or new needs emerge.",
	],
];

const faqs = [
	[
		"Can a live-in carer support someone having chemotherapy?",
		"Yes. A carer can help with practical preparation, transport, meals, hydration, personal care and rest around treatment. Clinical advice and concerns about side effects must always be directed to the person's oncology team or the emergency contact they have provided.",
	],
	[
		"Can care change from week to week?",
		"Cancer treatment can affect energy and symptoms differently over time. The care plan can be reviewed as needs change, with more help on difficult days and encouragement for independence when the person feels stronger.",
	],
	[
		"Does a carer give cancer medication?",
		"Medication support depends on the agreed care plan, the medicine involved and the carer's training. Edyn assesses this before care begins and works within clear professional boundaries.",
	],
	[
		"Can live-in care work alongside district nurses or hospice teams?",
		"Yes. Live-in care provides day-to-day non-clinical support and can work alongside GPs, district nurses, oncology teams and specialist palliative services. It does not replace nursing or medical care.",
	],
	[
		"Is cancer care only for the end of life?",
		"No. Families may arrange care during treatment, after surgery, through recovery or while living with cancer over the longer term. Support is shaped around what the person needs now.",
	],
];

export default function CancerCare() {
	return (
		<div className="container edyn-redesign-shell">
			<Seo
				title="Cancer Live-in Care at Home | Edyn Care"
				description="Personalised live-in cancer care supporting treatment, recovery and everyday life at home, managed by Edyn's experienced care team."
				path="/condition/cancer/"
				image="https://www.edyn.care/assets/marketing-site/images/condition_cancer_splash.jpeg"
				service={{ name: "Cancer live-in care", serviceType: "Cancer care at home" }}
				breadcrumbs={[
					{ name: "Home", path: "/" },
					{ name: "Cancer care", path: "/condition/cancer/" },
				]}
			/>
			<Header />

			<main
				id="main-content"
				className="edyn-dementia edyn-dementia--textured edyn-cancer"
			>
				<section className="dementia-hero">
					<div className="site-shell">
						<div className="dementia-hero__panel">
							<div className="dementia-hero__copy">
								<p className="eyebrow eyebrow--light">Cancer care at home</p>
								<h1>
									Care that makes room for the person, not just the diagnosis.
								</h1>
							</div>
							<div className="dementia-hero__summary">
								<p className="dementia-hero__lead">
									One-to-one support can make treatment, recovery and daily life
									feel more manageable in the comfort of home.
								</p>
								<ul>
									<li>Care shaped around treatment and energy levels</li>
									<li>A professional carer in the home</li>
									<li>Ongoing oversight from a Care Manager</li>
								</ul>
								<div className="button-row">
									<Link className="button button--cream" to="/care-consultation/">
										Talk to a Care Advisor
									</Link>
									<a
										className="button button--text-light"
										href="#cancer-support"
									>
										Explore support <span aria-hidden="true">&#8595;</span>
									</a>
								</div>
							</div>
						</div>

						<div className="dementia-hero__visual">
							<img
								src={`${imagePath}/condition_cancer_splash.jpeg`}
								alt="An older woman resting during cancer treatment"
								width="1920"
								height="1280"
							/>
							<div className="dementia-hero__note">
								<span aria-hidden="true">&#10003;</span>
								<p>
									<strong>Support on the difficult days.</strong>
									Independence on the better ones.
								</p>
							</div>
						</div>
					</div>
				</section>

				<section className="condition-trustpilot" aria-label="Customer reviews">
					<div className="site-shell condition-trustpilot__inner">
						<TrustpilotInline />
					</div>
				</section>

				<section
					className="dementia-assurance"
					aria-label="Edyn care assurances"
				>
					<div className="site-shell dementia-assurance__grid">
						<p>
							<strong>One-to-one</strong>
							<span>support in the home</span>
						</p>
						<p>
							<strong>Fully managed</strong>
							<span>by an Edyn care team</span>
						</p>
						<p>
							<strong>CQC registered</strong>
							<span>care across England</span>
						</p>
					</div>
				</section>

				<section className="dementia-person section-space">
					<div className="site-shell dementia-person__grid">
						<p className="eyebrow">Living beyond the appointment</p>
						<div>
							<h2>
								Cancer changes the rhythm of life. Good care learns the new one.
							</h2>
							<div className="dementia-person__copy">
								<p>
									A diagnosis can bring appointments, uncertainty and days when
									even familiar routines take more energy. No two experiences
									are the same, and needs can shift throughout treatment and
									recovery.
								</p>
								<p>
									Live-in care offers a steady presence at home: someone who
									understands the plan, notices changes and helps everyday life
									continue around the person and their priorities.
								</p>
							</div>
						</div>
					</div>
				</section>

				<section
					id="cancer-support"
					className="dementia-support section-space section-dark"
				>
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow eyebrow--light">Everyday support</p>
								<h2>Thoughtful help before, during and after treatment.</h2>
							</div>
							<p>
								Care works alongside the oncology team and follows the clinical
								guidance, medication plan and escalation contacts they provide.
							</p>
						</div>
						<div className="dementia-support__grid">
							{supportAreas.map(([number, title, copy]) => (
								<article key={title}>
									<span>{number}</span>
									<h3>{title}</h3>
									<p>{copy}</p>
								</article>
							))}
						</div>
					</div>
				</section>

				<section className="dementia-understanding section-space">
					<div className="site-shell dementia-understanding__grid">
						<div className="dementia-understanding__intro">
							<p className="eyebrow">Responding to change</p>
							<h2>Knowing the person makes changes easier to notice.</h2>
							<p>
								Cancer and its treatment can affect the whole person. A
								consistent carer can observe day-to-day changes and share
								concerns through the agreed care and clinical channels.
							</p>
							<a
								className="dementia-source"
								href="https://www.nhs.uk/conditions/cancer/"
								target="_blank"
								rel="noopener noreferrer"
							>
								Read NHS cancer guidance <span aria-hidden="true">&#8599;</span>
							</a>
						</div>
						<ul className="dementia-understanding__signs">
							{changesToShare.map((change, index) => (
								<li key={change}>
									<span>{String(index + 1).padStart(2, "0")}</span>
									{change}
								</li>
							))}
						</ul>
					</div>
				</section>

				<section className="dementia-facts section-space">
					<div className="site-shell dementia-facts__panel">
						<div className="dementia-facts__title">
							<img
								src={`${imagePath}/we-care-image-2.jpg`}
								alt=""
								loading="lazy"
							/>
							<div>
								<p className="eyebrow eyebrow--light">The wider picture</p>
								<h2>Cancer in the UK</h2>
							</div>
						</div>
						<div className="dementia-facts__grid">
							<p>
								<strong>More than 200</strong>
								<span>different types of cancer</span>
							</p>
							<p>
								<strong>Almost 3.5 million</strong>
								<span>people are living with cancer in the UK</span>
							</p>
							<p>
								<strong>Every 75 seconds</strong>
								<span>someone is diagnosed in the UK on average</span>
							</p>
							<p>
								<strong>1 in 2</strong>
								<span>people diagnosed now survive for at least 10 years</span>
							</p>
							<p>
								<strong>Treatment is personal</strong>
								<span>and depends on the cancer type and individual</span>
							</p>
							<p>
								<strong>Home still matters</strong>
								<span>through treatment, recovery and longer-term care</span>
							</p>
						</div>
					</div>
				</section>

				<section className="dementia-types section-space section-soft">
					<div className="site-shell dementia-types__grid">
						<div>
							<p className="eyebrow">Care through change</p>
							<h2>Support can begin at any point in the cancer journey.</h2>
							<p>
								The right level of care depends on the person's treatment,
								symptoms, home and the people already around them.
							</p>
						</div>
						<div className="dementia-types__list">
							{careStages.map(([title, copy], index) => (
								<details key={title} open={index === 0}>
									<summary>
										<span>{String(index + 1).padStart(2, "0")}</span>
										{title}
									</summary>
									<p>{copy}</p>
								</details>
							))}
						</div>
					</div>
				</section>

				<section className="dementia-home section-space">
					<div className="site-shell dementia-home__grid">
						<div className="dementia-home__visual cancer-home__visual">
							<img
								src={`${imagePath}/carer-and-woman.png`}
								alt="A professional carer spending time with a woman at home"
								loading="lazy"
							/>
						</div>
						<div className="dementia-home__copy">
							<p className="eyebrow">Preparing the home</p>
							<h2>Make home easier on low-energy days.</h2>
							<p>
								Before care begins, we look at routines, risks and practical
								ways to make the home feel comfortable, organised and
								reassuring.
							</p>
							<div className="dementia-home__principles">
								{homePrinciples.map(([title, copy]) => (
									<article key={title}>
										<h3>{title}</h3>
										<p>{copy}</p>
									</article>
								))}
							</div>
						</div>
					</div>
				</section>

				<section className="dementia-managed section-space section-dark">
					<div className="site-shell">
						<div className="dementia-managed__heading">
							<p className="eyebrow eyebrow--light">
								Fully managed cancer care
							</p>
							<h2>
								One carer in the home. A wider team keeping care on track.
							</h2>
						</div>
						<div className="dementia-managed__steps">
							<article>
								<span>01</span>
								<h3>Understand</h3>
								<p>
									A Care Manager learns about the diagnosis, treatment plan,
									routines, risks and what matters to the person.
								</p>
							</article>
							<article>
								<span>02</span>
								<h3>Match</h3>
								<p>
									We select a professional carer whose experience, manner and
									approach fit the person's needs and household.
								</p>
							</article>
							<article>
								<span>03</span>
								<h3>Review</h3>
								<p>
									As treatment or symptoms change, the wider Edyn team can
									review support and update the care plan with the family.
								</p>
							</article>
						</div>
						<Link className="button button--cream" to="/approach/">
							See how Edyn works
						</Link>
					</div>
				</section>

				<section className="dementia-faq section-space">
					<div className="site-shell dementia-faq__grid">
						<div>
							<p className="eyebrow">Questions families ask</p>
							<h2>Clear answers when circumstances are changing.</h2>
							<p>
								Cancer care can involve several teams and a great deal of new
								information. These are some common questions about support at
								home.
							</p>
						</div>
						<div className="dementia-faq__list">
							{faqs.map(([question, answer]) => (
								<details key={question}>
									<summary>{question}</summary>
									<p>{answer}</p>
								</details>
							))}
						</div>
					</div>
				</section>
			</main>

			<Footer />
		</div>
	);
}
